/**
 * Level 1 — the Habitable Zone.
 *
 * Where the Lobby is all walls, this is all span. One open slab is divided into
 * bays by a few full-length structural walls with wide gaps, a column grid runs
 * unbroken across the whole level so sight lines stay readable, and racking
 * rows break the bays into long aisles you can see all the way down — and that
 * something else can see all the way down too.
 */

import { Cell } from '../../core/types';
import { scatterDamp, scatterOpenCeilings } from './decorate';
import {
  addRegion,
  fillRect,
  inset,
  interiorRect,
  rectsOverlap,
  strokeRect,
  type GenSite,
  type Rect,
} from './site';
import { applyWrongness } from './wrongness';

/** Smallest bay dimension in cells. Below this the space stops feeling like a span. */
const MIN_BAY = 16;

/** Carve the Habitable Zone profile into `site`. */
export function generateWarehouse(site: GenSite): void {
  const { grid, rng } = site;
  const floor = inset(interiorRect(grid), 1);
  fillRect(grid, floor, Cell.Floor);

  const bays: Rect[] = [];
  splitBays(site, floor, rng.int(2, 3), bays);

  // One column grid for the whole building, not one per bay: the columns lining
  // up across a structural wall is what sells the scale.
  const step = rng.int(4, 5);
  const ox = floor.x + 2;
  const oy = floor.y + 2;
  for (let y = oy; y < floor.y + floor.h - 1; y += step) {
    for (let x = ox; x < floor.x + floor.w - 1; x += step) {
      if (grid.at(x, y) === Cell.Floor) grid.set(x, y, Cell.Pillar);
    }
  }

  const offices: Rect[] = [];
  for (const bay of bays) {
    addRegion(site, bay, bay.w * bay.h >= 900 ? 'atrium' : 'hall');
    // Only some bays are racked out. The empty ones are the point of the level:
    // forty metres of nothing with a light at the far end.
    if (rng.chance(0.55)) carveRacking(site, bay);
    else carvePartitions(site, bay);
    if (rng.chance(0.75)) placeOffices(site, bay, rng.int(1, 3), offices);
  }

  // Less of it than the Lobby gets, and weighted toward the two operations that
  // read across a forty-metre span: a blank block standing in the middle of a
  // bay, and a wall that grows out of a racking run and stops.
  applyWrongness(site, {
    innerRooms: [4, 7],
    doorsToNothing: [3, 6],
    perforatedWalls: [1, 2],
    pillarsInDoorways: [1, 3],
    wallFins: [8, 14],
    tightPillarPatches: [2, 4],
  });

  scatterDamp(site, rng.int(8, 16), 6, 30);
  scatterOpenCeilings(site, rng.int(1, 3), 2, 4);
}

/**
 * Recursive bay split. Each cut is a full-height wall with two or three wide
 * gaps, so the level reads as separate halls without ever being a maze.
 */
function splitBays(site: GenSite, rect: Rect, depth: number, out: Rect[]): void {
  const { grid, rng } = site;
  const canX = rect.w >= 2 * MIN_BAY + 1;
  const canY = rect.h >= 2 * MIN_BAY + 1;
  if (depth <= 0 || (!canX && !canY)) {
    out.push(rect);
    return;
  }
  const vertical = !canY ? true : !canX ? false : rect.w >= rect.h;
  const span = vertical ? rect.w : rect.h;
  const origin = vertical ? rect.x : rect.y;
  const cut = origin + rng.int(MIN_BAY, span - 1 - MIN_BAY);

  if (vertical) {
    fillRect(grid, { x: cut, y: rect.y, w: 1, h: rect.h }, Cell.Wall);
  } else {
    fillRect(grid, { x: rect.x, y: cut, w: rect.w, h: 1 }, Cell.Wall);
  }

  // Wide gaps rather than doorways: you should be able to drive through.
  const crossSpan = vertical ? rect.h : rect.w;
  const crossOrigin = vertical ? rect.y : rect.x;
  const gaps = rng.int(2, 3);
  for (let g = 0; g < gaps; g++) {
    const band = crossSpan / gaps;
    const centre = crossOrigin + Math.round(band * (g + 0.5) + rng.range(-band * 0.25, band * 0.25));
    const width = rng.int(2, 4);
    for (let k = 0; k < width; k++) {
      const t = centre - (width >> 1) + k;
      if (t <= crossOrigin || t >= crossOrigin + crossSpan - 1) continue;
      if (vertical) grid.set(cut, t, Cell.Floor);
      else grid.set(t, cut, Cell.Floor);
    }
  }

  if (vertical) {
    splitBays(site, { x: rect.x, y: rect.y, w: cut - rect.x, h: rect.h }, depth - 1, out);
    splitBays(site, { x: cut + 1, y: rect.y, w: rect.x + rect.w - cut - 1, h: rect.h }, depth - 1, out);
  } else {
    splitBays(site, { x: rect.x, y: rect.y, w: rect.w, h: cut - rect.y }, depth - 1, out);
    splitBays(site, { x: rect.x, y: cut + 1, w: rect.w, h: rect.y + rect.h - cut - 1 }, depth - 1, out);
  }
}

/**
 * Short partition walls in parallel runs. The aisles between them are the long
 * sight lines; the breaks in each run are what stops the bay being a comb, and
 * are why nothing here ever fully encloses anything.
 */
function carveRacking(site: GenSite, bay: Rect): void {
  const { grid, rng } = site;
  const inner = inset(bay, 3);
  if (inner.w < 6 || inner.h < 6) return;
  const horizontal = rng.chance(0.5);
  const pitch = rng.int(4, 6);
  const laneSpan = horizontal ? inner.h : inner.w;
  const runSpan = horizontal ? inner.w : inner.h;
  const laneOrigin = horizontal ? inner.y : inner.x;
  const runOrigin = horizontal ? inner.x : inner.y;

  for (let lane = laneOrigin; lane < laneOrigin + laneSpan; lane += pitch) {
    if (rng.chance(0.22)) continue;
    let t = runOrigin + rng.int(0, 3);
    while (t < runOrigin + runSpan) {
      const run = rng.int(4, 14);
      const gap = rng.int(2, 5);
      if (rng.chance(0.85)) {
        for (let k = 0; k < run && t + k < runOrigin + runSpan; k++) {
          if (horizontal) grid.set(t + k, lane, Cell.Wall);
          else grid.set(lane, t + k, Cell.Wall);
        }
      }
      t += run + gap;
    }
  }
}

/**
 * The alternative to racking: a scatter of short free-standing walls and stacked
 * crates. It keeps the bay open enough to see across while making sure you can
 * never see all of it, which is the difference between a room and a warehouse.
 */
function carvePartitions(site: GenSite, bay: Rect): void {
  const { grid, rng } = site;
  const inner = inset(bay, 3);
  if (inner.w < 8 || inner.h < 8) return;
  const walls = Math.round((inner.w * inner.h) / 190);
  for (let i = 0; i < walls; i++) {
    const horizontal = rng.chance(0.5);
    const len = rng.int(6, 20);
    if (horizontal) {
      const x = rng.int(inner.x, Math.max(inner.x, inner.x + inner.w - len));
      const y = rng.int(inner.y, inner.y + inner.h - 1);
      for (let k = 0; k < len && x + k < inner.x + inner.w; k++) grid.set(x + k, y, Cell.Wall);
    } else {
      const x = rng.int(inner.x, inner.x + inner.w - 1);
      const y = rng.int(inner.y, Math.max(inner.y, inner.y + inner.h - len));
      for (let k = 0; k < len && y + k < inner.y + inner.h; k++) grid.set(x, y + k, Cell.Wall);
    }
  }
  const crates = Math.round((inner.w * inner.h) / 260);
  for (let i = 0; i < crates; i++) {
    const w = rng.int(2, 4);
    const h = rng.int(2, 4);
    const x = rng.int(inner.x, inner.x + inner.w - w);
    const y = rng.int(inner.y, inner.y + inner.h - h);
    fillRect(grid, { x, y, w, h }, Cell.Wall);
  }
}

/** Site offices: small walled rooms with one door, good places to keep supplies. */
function placeOffices(site: GenSite, bay: Rect, count: number, taken: Rect[]): void {
  const { grid, rng } = site;
  for (let i = 0; i < count; i++) {
    for (let tries = 0; tries < 30; tries++) {
      const w = rng.int(5, 9);
      const h = rng.int(4, 8);
      if (bay.w < w + 4 || bay.h < h + 4) break;
      const r: Rect = {
        x: rng.int(bay.x + 1, bay.x + bay.w - w - 2),
        y: rng.int(bay.y + 1, bay.y + bay.h - h - 2),
        w,
        h,
      };
      if (taken.some((o) => rectsOverlap(r, o, 2))) continue;
      taken.push(r);

      strokeRect(grid, r, Cell.Wall);
      fillRect(grid, inset(r, 1), Cell.Floor);
      punchOfficeDoor(site, r);
      addRegion(site, r, w * h <= 24 ? 'closet' : 'room');

      // Half of them have a store cupboard in the corner.
      if (w >= 7 && h >= 6 && rng.chance(0.5)) {
        const cw = rng.int(2, 3);
        const ch = rng.int(2, 3);
        const cr: Rect = { x: r.x + 1, y: r.y + 1, w: cw, h: ch };
        strokeRect(grid, { x: cr.x - 1, y: cr.y - 1, w: cw + 2, h: ch + 2 }, Cell.Wall);
        fillRect(grid, cr, Cell.Floor);
        grid.set(cr.x + cw, cr.y + (ch >> 1), Cell.Doorway);
        addRegion(site, cr, 'closet');
      }
      break;
    }
  }
}

function punchOfficeDoor(site: GenSite, r: Rect): void {
  const { grid, rng } = site;
  for (let tries = 0; tries < 24; tries++) {
    const side = rng.int(0, 3);
    let x: number;
    let y: number;
    if (side === 0) {
      x = rng.int(r.x + 1, r.x + r.w - 2);
      y = r.y;
    } else if (side === 1) {
      x = rng.int(r.x + 1, r.x + r.w - 2);
      y = r.y + r.h - 1;
    } else if (side === 2) {
      x = r.x;
      y = rng.int(r.y + 1, r.y + r.h - 2);
    } else {
      x = r.x + r.w - 1;
      y = rng.int(r.y + 1, r.y + r.h - 2);
    }
    const ox = side === 2 ? x - 1 : side === 3 ? x + 1 : x;
    const oy = side === 0 ? y - 1 : side === 1 ? y + 1 : y;
    if (!grid.isWalkable(ox, oy)) continue;
    grid.set(x, y, Cell.Doorway);
    return;
  }
  // Nothing walkable outside any wall: connectivity repair will find a way in.
}
